import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  CustomInput,
  Form,
  FormGroup,
  FormText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { BtnOutlineGreen, BtnOutlineRed } from '../../../styled-components';
import { useAuth } from '../../../commons/Auth';
import { useAlert } from 'react-alert';
import JSZip from 'jszip';

const INITAL_FILE_FORM_VALUES = {
  expireDate: '',
  file: '',
  needed: false,
  type: '',
  noExpireDate: false,
  rasCardId: '',
};

function AddRASFileButton({ profile = {}, uploadRASZip, condo, RASTypes }) {
  const [t] = useTranslation('FILES');
  const [{ newIsAdmin } = {}] = useAuth();
  const alert = useAlert();
  const [addNewFileModal, setAddNewFileModal] = useState(false);
  const [formValues, setFormValues] = useState(INITAL_FILE_FORM_VALUES);
  const [errors, setErrors] = useState({
    file: false,
    zip: false,
    mapping: false,
    parsing: false,
  });
  const [firstTry, setFirtsTry] = useState(true);

  useEffect(() => {}, [errors]);

  const toggleAddNewFileModal = () => {
    setFormValues(INITAL_FILE_FORM_VALUES);
    setAddNewFileModal(!addNewFileModal);
    setFirtsTry(true);
    setErrors({ file: false, zip: false, mapping: false, parsing: false });
  };

  async function handleFileChange() {
    const file = formValues.file;

    const fileSizeMB = file && file.size ? file.size / (1024 * 1024) : 0;
    if (fileSizeMB > 100) {
      alert.error('Il file ZIP supera la dimensione massima di 100 MB');
      setErrors({ ...errors, file: true });
      return;
    }

    if (
      file &&
      (file.type === 'application/zip' ||
        file.type === 'application/x-zip-compressed')
    ) {
      try {
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(file);

        const mappingFile = Object.keys(zipContent.files).find((path) =>
          path.endsWith('mapping.json')
        );

        if (!mappingFile) {
          //   alert.error('File mapping.json mancante nello ZIP');
          setErrors({ ...errors, mapping: true });
          return;
        }

        const mappingContent = await zipContent.files[mappingFile].async(
          'text'
        );
        let mapping;
        try {
          mapping = JSON.parse(mappingContent);
        } catch (e) {
          setErrors({ ...errors, parsing: true });
          //   alert.error('Errore nel parsing del file mapping.json');
          return;
        }

        const formDataArray = [];

        for (const filePath of Object.keys(zipContent.files)) {
          const zipFile = zipContent.files[filePath];

          if (zipFile.dir || filePath === mappingFile) continue;

          const filename = filePath.split('/').pop();

          const fileMapping = mapping.find((m) => m.fileName === filename);
          if (!fileMapping) {
            console.warn(`Mapping non trovato per il file: ${filename}`);
            continue;
          }

          const content = await zipFile.async('blob');
          const fileObject = new File([content], filename, {
            type: 'application/octet-stream',
          });

          const rasType = RASTypes.find(
            (rt) => rt.name.toLowerCase() === fileMapping.rasType.toLowerCase()
          );
          if (!rasType) {
            console.warn(`RAS Type non trovato per: ${fileMapping.rasType}`);
            continue;
          }

          const formData = new FormData();
          formData.append('file', fileObject);
          formData.append('keyName', filename);
          formData.append('rasCardId', rasType._id);
          formData.append('condoId', condo._id);
          formData.append('newIsAdmin', newIsAdmin);
          formDataArray.push(formData);
        }

        setFirtsTry(false);
        errors.file = false;
        errors.zip = false;
        errors.mapping = false;
        setErrors(errors);

        if (file === '') errors.file = true;

        const arrayErrors = Object.values(errors);
        if (!arrayErrors.includes(true)) {
          uploadRASZip(formDataArray, toggleAddNewFileModal);
        }
      } catch (error) {
        console.error('Errore nella lettura del file ZIP:', error);
        alert.error('Errore nella lettura del file ZIP');
      }
    } else {
      //   alert.info('Il file deve essere in formato .zip');
      setErrors({ ...errors, zip: true });
      return;
    }
  }

  return (
    <React.Fragment>
      <Button
        target="_blank"
        color="primary"
        style={{ marginTop: 0, marginRight: 10 }}
        onClick={(event) => {
          event.stopPropagation();
          toggleAddNewFileModal();
        }}
      >
        Aggiungi RAS
      </Button>

      <Modal
        isOpen={addNewFileModal}
        toggle={toggleAddNewFileModal}
        className="new-file-modal"
      >
        <ModalHeader toggle={toggleAddNewFileModal}>
          {t('FILES:ADD')}
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleFileChange();
          }}
        >
          <ModalBody>
            <Row>
              <Col sm="12">
                <FormGroup>
                  <Label for="selectFile">
                    {t('FILES:CHOOSE_FILE')}
                    &nbsp;({t('FILES:MAX_UPLOAD_FILE_SIZE_RAS')})
                  </Label>
                  <CustomInput
                    type="file"
                    id="fileCommercialContract"
                    name="fileCommercialContract"
                    accept=".zip"
                    onChange={(event) =>
                      setFormValues({
                        ...formValues,
                        file: event.target.files[0],
                      })
                    }
                  />
                  {formValues.file === '' && (
                    <FormText className="error-message">
                      {t('FILE_ZIP_ERROR')}
                    </FormText>
                  )}
                  {errors.mapping === true && (
                    <FormText className="error-message">
                      {t('MAPPING_ERROR')}
                    </FormText>
                  )}
                  {errors.parsing === true && (
                    <FormText className="error-message">
                      {t('PARSING_ERROR')}
                    </FormText>
                  )}
                  {errors.zip === true && (
                    <FormText className="error-message">
                      {t('ZIP_ERROR')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <BtnOutlineRed
              type="button"
              className="uppercase"
              onClick={toggleAddNewFileModal}
            >
              {t('COMMON:CANCEL')}
            </BtnOutlineRed>
            <BtnOutlineGreen type="submit" className="uppercase">
              {t('COMMON:CONFIRM')}
            </BtnOutlineGreen>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
}

export default AddRASFileButton;
